export const useWorkspaceStore = defineStore('workspace', {
  state: () => {
    return {
      _id: null,
      name: null,
      identity: {
        primaryColor: null,
        logo: null,
        font: null,
      },
      plan: null,
      globalTemplates: {
        email: [],
        page: [],
      },
      integrations: {},
      restrictions: {},
      distributor: null,

      isLoaded: false,
      loadFailed: false,
      isIntegrationStripeConnecting: false,
      isIntegrationGoogleCalendarConnecting: false,
    };
  },

  actions: {
    // Workspace
    async fetchWorkspace() {
      const workspace = await useApi.get('/workspace').catch(() => {
        useToast.error(useT('common.errorLoadingData'));
        this.loadFailed = true;
      });

      if (workspace) {
        this._id = workspace._id;
        this.name = workspace.name;
        this.identity = workspace.identity;
        this.globalTemplates = workspace.globalTemplates;
        this.integrations = workspace.integrations;
        this.restrictions = workspace.restrictions || {};
        this.plan = workspace.plan;
        this.distributor = workspace.distributor;

        this.loadFailed = false;
        this.isLoaded = true;

        useUsersStore().fetchUsersCount();

        return workspace;
      }

      return false;
    },

    async createWorkspace(body) {
      const response = await useApi.post('/public/workspace', { body }).catch((error) => {
        if (error.response?.status === 409) {
          useToast.error(useT('auth.alreadyRegistered'), {
            dangerouslyUseHTMLString: true,
          });
        } else if (error.response?.status === 400 && error.response?.data?.message?.includes('Turnstile')) {
          useToast.error(useT('auth.register.verificationFailed'));
        } else {
          useToast.error(useT('common.errorSavingChanges'));
        }
      });

      return response;
    },

    async getGlobalTemplates(type) {
      const response = await useApi.get('/workspace/global-templates', {
        params: {
          type,
        },
      }).catch(() => {
        useToast.error(useT('common.errorLoadingData'));
      });

      return response;
    },

    async updateWorkspace(body) {
      const response = await useApi.put('/workspace', { body }).catch(() => {
        useToast.error(useT('common.errorSavingChanges'));
      });

      if (response) {
        Object.entries(body)?.forEach(([key, value]) => {
          this[key] = value;
        });
      }

      return response;
    },

    // Workspace/Airtable
    async getAirtable() {
      const response = await useApi.get('/workspace/airtable').catch(() => {
        useToast.error(useT('common.errorLoadingData'));
      });

      return response;
    },

    async updateAirtable(body) {
      const response = await useApi.put('/workspace/airtable', { body }).catch(() => {
        useToast.error(useT('common.errorSavingChanges'));
      });

      return response;
    },
    async getAirtableTables({ apiKey, baseId }) { // for integration settings
      const response = await useApi.get('/workspace/airtable/tables', {
        headers: {
          'x-airtable-api-key': apiKey,
          'x-airtable-base-id': baseId,
        },
      }).catch(() => {
        useToast.error(useT('common.errorLoadingData'));
      });

      return response;
    },

    async getAirtableTable() {
      const response = await useApi.get('/workspace/airtable/table').catch(() => {
        useToast.error(useT('common.errorLoadingData'));
      });

      return response;
    },
    async getAirtableTableView(viewId) {
      const response = await useApi.get(`/workspace/airtable/table/views/${viewId}`).catch(() => {
        useToast.error(useT('common.errorLoadingData'));
      });

      return response;
    },

    // Workspace/CustomDomain
    async addCustomDomain(body) {
      const response = await useApi.post('/workspace/custom-domain', { body }).catch((error) => {
        if (error.response?.status === 400) {
          useToast.error(useT('workspace.customDomain.notAllowed'));
        } else if (error.response?.status === 409) {
          useToast.error(useT('workspace.customDomain.alreadyLinked'));
        } else {
          useToast.error(useT('common.errorSavingChanges'));
        }
      });

      return response;
    },
    async checkCustomDomain() {
      const response = await useApi.get('/workspace/custom-domain/check').catch(() => {
        useToast.error(useT('workspace.customDomain.error'));
      });

      return response;
    },
    async removeCustomDomain() {
      const response = await useApi.delete('/workspace/custom-domain').catch(() => {
        useToast.error(useT('workspace.customDomain.errorRemove'));
      });

      return response;
    },

    // Workspace/ApiKeys
    async getApiKeys() {
      const response = await useApi.get('/workspace/api-keys').catch(() => {
        useToast.error(useT('common.errorLoadingData'));
      });

      return response;
    },
    async createApiKey() {
      const response = await useApi.post('/workspace/api-keys').catch(() => {
        useToast.error(useT('common.errorSavingChanges'));
      });

      return response;
    },
    async deleteApiKey(id) {
      const response = await useApi.delete(`/workspace/api-keys/${id}`).catch(() => {
        useToast.error(useT('common.errorSavingChanges'));
      });

      return response;
    },

    // Workspace/GlobalTemplates
    async addGlobalTemplate(payload) {
      const response = await useApi.post('/workspace/global-templates', {
        body: payload,
      }).catch(() => {
        useToast.error(useT('workspace.template.errorAdd'));
      });

      if (response) {
        this.globalTemplates = response.globalTemplates;
      }

      return response;
    },

    async removeGlobalTemplate(payload) {
      const response = await useApi.delete('/workspace/global-templates', {
        body: payload,
      }).catch(() => {
        useToast.error(useT('workspace.template.errorRemove'));
      });

      if (response) {
        this.globalTemplates = response.globalTemplates;
      }

      return response;
    },

    // Workspace/Stripe
    async openCustomerPortal({ cancelSubscription } = {}) {
      useDialogStore().open('loading', {});
      const response = await useApi.get('/workspace/customer-portal', {
        query: {
          cancelSubscription,
        },
      }).catch(() => {
        useToast.error(useT('poly.errorOccurred'));
      });

      if (response) {
        window.location.href = response.url;
        return response;
      }

      useDialogStore().close('loading');
    },

    async openIntegrationsStripeOauthUrl({ openSettingsDialog } = {}) {
      const response = await useApi.get('/workspace/integrations/stripe/oauth/url', {
        query: {
          currentPath: encodeURIComponent(useRoute().fullPath),
          openSettingsDialog: openSettingsDialog || undefined,
        },
      }).catch(() => {
        useToast.error(useT('common.errorLoadingData'));
      });

      if (response) {
        window.location.href = response.url;
      }
    },

    async connectIntegrationsStripe(code) {
      this.isIntegrationStripeConnecting = true;

      const response = await useApi.post('/workspace/integrations/stripe/connect', {
        body: {
          code,
        },
      }).catch(() => {
        useToast.error(useT('workspace.integrations.stripe.connectError'));
      });

      if (response) {
        await this.fetchWorkspace();
      }

      this.isIntegrationStripeConnecting = false;

      return response;
    },

    async disconnectIntegrationsStripe() {
      const response = await useApi.delete('/workspace/integrations/stripe/disconnect').catch(() => {
        useToast.error(useT('workspace.integrations.stripe.disconnectError'));
      });

      if (response) {
        await this.fetchWorkspace();
      }

      return response;
    },

    // `code` comes from the Google Identity Services popup opened by the
    // integration card (same code flow as the Google login).
    async connectIntegrationsGoogleCalendar(code) {
      this.isIntegrationGoogleCalendarConnecting = true;

      const response = await useApi.post('/workspace/integrations/google-calendar/connect', { body: { code } }).catch((error) => {
        // A 4xx body is the API's own plain-text reason (e.g. the calendar
        // permission left unticked on Google's consent screen); a 5xx body
        // may be a raw upstream error, so that one gets the generic message.
        const reason = error?.response?.status < 500 ? error.response._data : null;
        useToast.error((typeof reason === 'string' && reason) || useT('workspace.integrations.googleCalendar.connectError'));
      });

      if (response) {
        await this.fetchWorkspace();
      }

      this.isIntegrationGoogleCalendarConnecting = false;

      return Boolean(response);
    },

    async disconnectIntegrationsGoogleCalendar() {
      const response = await useApi.delete('/workspace/integrations/google-calendar/disconnect').catch(() => {
        useToast.error(useT('workspace.integrations.googleCalendar.disconnectError'));
      });

      if (response) {
        await this.fetchWorkspace();
      }

      return response;
    },
  },

  getters: {
    getCustomOrigin: (state) => {
      return (event) => {
        if (state.integrations?.customDomain?.url /* && event?.plan?.config?.tier === 'enterprise' */ && useRuntimeConfig().public.nodeEnv === 'production') {
          return `https://${state.integrations.customDomain.url}`;
        }

        return useRequestURL().origin;
      };
    },

    getCurrency: () => {
      return 'usd';
    },

    isSandbox: (state) => state.plan?.tier === 'distributor_sandbox',
    isInTier(state) {
      const currentTier = state.plan?.tier?.split('_')?.[0];
      if (!currentTier) {
        console.warn('No tier found');
      }

      return (tier) => Array.isArray(tier) ? tier.includes(currentTier) : tier === currentTier;
    },

    // Mirrors the API's create-time gate (`throwIfEventCreationDisabled`), so
    // the UI stops offering an action the server would refuse.
    isEventCreationDisabled: (state) => Boolean(state.restrictions?.isEventCreationDisabled),

    hasOverlimitUsers: (state) => {
      const tierName = state.plan?.tier?.split('_')?.[0];

      if (!tierName || !useUsersStore().usersCount) {
        return false;
      }

      return (tierName === 'free' || tierName === 'pro') && useUsersStore().usersCount > 1;
    },
  },
});
